import './AnimatedBoard.css'

import { BAR_POINT, OFF_POINT } from '@kfiroo/backgammon-core'
import { useEffect, useLayoutEffect, useRef } from 'react'

import { Board } from './Board'
import { AnimatedMove, BoardInfo, PlayerType } from './GameApi/BaseGameApi'
import { useAnimatedBoard } from './hooks/useAnimatedBoard'
import {
  prefersReducedMotion,
  supportsWebAnimations,
} from './services/animationEnv'

const SLIDE_MS = 300

interface AnimatedBoardProps {
  board: BoardInfo
  liveMoves: AnimatedMove[]
  /** Authoritative turn pointer (0/1); a change resets the animation baseline. */
  turnKey: number
  dropTargets?: number[]
  hitTargets?: number[]
  selected?: number | null
  onLaneClicked?: (lane: number) => void
}

export function AnimatedBoard({
  board,
  liveMoves,
  turnKey,
  ...boardProps
}: AnimatedBoardProps) {
  const instant = prefersReducedMotion() || !supportsWebAnimations()
  const { displayedBoard, flying, onArrived } = useAnimatedBoard(
    board,
    liveMoves,
    instant,
    turnKey,
  )
  const rootRef = useRef<HTMLDivElement>(null)
  const cleanupRef = useRef<(() => void) | null>(null)

  // Start the slide when a move begins flying. `onArrived()` applies the move
  // (re-rendering the real destination checker); the overlay is torn down
  // separately, below.
  useEffect(() => {
    if (!flying) {
      return
    }
    const root = rootRef.current
    if (!root || !supportsWebAnimations()) {
      onArrived()
      return
    }
    let cancelled = false
    const { done, cleanup } = runMoveAnimation(root, flying)
    cleanupRef.current = cleanup
    void done.then(() => {
      if (!cancelled) {
        onArrived()
      }
    })
    return () => {
      cancelled = true
    }
  }, [flying, onArrived])

  // Remove the overlay only AFTER the move has been applied and committed
  // (flying is back to null), synchronously before paint. By then the real
  // destination checker is painted (no end gap) and the source checker is gone
  // (so restoring hidden nodes never flashes one back at the start).
  useLayoutEffect(() => {
    if (flying) {
      return
    }
    cleanupRef.current?.()
    cleanupRef.current = null
  }, [flying, displayedBoard])

  return (
    <div className="animated-board" ref={rootRef}>
      <Board board={displayedBoard} {...boardProps} />
    </div>
  )
}

interface MoveAnimation {
  /** Resolves once every slide (the move, plus the victim on a hit) finishes. */
  done: Promise<void>
  /** Restores any hidden checkers and removes the overlay. Idempotent. */
  cleanup: () => void
}

/**
 * Slides each moved checker between its real on-board positions: the exact
 * checker that leaves the source point, and the exact slot it lands on. The
 * source checker is hidden for the flight so it lifts off rather than leaving a
 * duplicate behind; on a hit, the victim slides off to the bar the same way.
 */
function runMoveAnimation(
  root: HTMLElement,
  move: AnimatedMove,
): MoveAnimation {
  const overlay = document.createElement('div')
  overlay.className = 'move-overlay'
  root.appendChild(overlay)

  const hidden: HTMLElement[] = []
  const slides: Promise<void>[] = [
    slideChecker(
      root,
      overlay,
      hidden,
      move.from,
      move.to,
      move.owner,
      move.isHit,
    ),
  ]
  if (move.isHit) {
    const defender =
      move.owner === PlayerType.OPPONENT
        ? PlayerType.PLAYER
        : PlayerType.OPPONENT
    slides.push(
      slideChecker(root, overlay, hidden, move.to, BAR_POINT, defender, false),
    )
  }

  let cleaned = false
  const cleanup = (): void => {
    if (cleaned) {
      return
    }
    cleaned = true
    for (const el of hidden) {
      // Source checkers are removed by the post-move re-render (skip those);
      // a reused node — e.g. a hit's destination — must be made visible again.
      if (el.isConnected) {
        el.style.visibility = ''
      }
    }
    overlay.remove()
  }

  return { done: Promise.all(slides).then(() => undefined), cleanup }
}

function slideChecker(
  root: HTMLElement,
  overlay: HTMLElement,
  hidden: HTMLElement[],
  from: number,
  to: number,
  owner: PlayerType,
  isHit: boolean,
): Promise<void> {
  const srcEl = anchorElement(root, from, owner)
  const dstEl = anchorElement(root, to, owner)
  if (!srcEl || !dstEl) {
    return Promise.resolve()
  }
  const rootRect = root.getBoundingClientRect()

  // Start at the exact checker that moves — the innermost (last) checker of the
  // source point/bar — and hide it for the flight so it appears to lift off.
  const movingEl = innermostChecker(srcEl)
  const startRect = (movingEl ?? srcEl).getBoundingClientRect()
  const start = center(startRect)
  const size = movingEl ? startRect.width : Math.max(20, startRect.width * 0.8)
  if (movingEl) {
    movingEl.style.visibility = 'hidden'
    hidden.push(movingEl)
  }

  // End at the exact slot the checker lands on (bar/off have no stack geometry).
  const end =
    to === BAR_POINT || to === OFF_POINT
      ? center(dstEl.getBoundingClientRect())
      : landingSpot(dstEl, size, isHit)

  const checker = document.createElement('div')
  checker.className = `checker checker-${owner} flying-checker`
  checker.style.width = `${size}px`
  checker.style.height = `${size}px`
  checker.style.left = `${start.x - rootRect.left - size / 2}px`
  checker.style.top = `${start.y - rootRect.top - size / 2}px`
  overlay.appendChild(checker)

  const dx = end.x - start.x
  const dy = end.y - start.y
  const anim = checker.animate(
    [
      { transform: 'translate(0px, 0px)' },
      { transform: `translate(${dx}px, ${dy}px)` },
    ],
    // `forwards` holds the checker at the destination after finishing (the
    // default would snap it back to the start). The overlay is removed by
    // cleanup() once the real destination checker has painted.
    { duration: SLIDE_MS, easing: 'ease-in-out', fill: 'forwards' },
  )

  return anim.finished.catch(() => undefined).then(() => undefined)
}

function anchorElement(
  root: HTMLElement,
  point: number,
  owner: PlayerType,
): Element | null {
  if (point === BAR_POINT) {
    return root.querySelector(`[data-anchor="bar-${owner}"]`)
  }
  if (point === OFF_POINT) {
    return root.querySelector(`[data-anchor="off-${owner}"]`)
  }
  return root.querySelector(`[data-point="${point}"]`)
}

function innermostChecker(el: Element): HTMLElement | null {
  const checkers = el.querySelectorAll<HTMLElement>('.checker')
  return checkers.length > 0 ? checkers[checkers.length - 1] : null
}

function center(rect: DOMRect): { x: number; y: number } {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
}

/**
 * The pixel slot a checker lands on at a board point, from the pre-move DOM:
 * a hit lands on the blot it replaces; an empty point lands at the outer edge;
 * otherwise it lands one slot further in than the current innermost checker
 * (clamped to the 5-high visual cap, where extra checkers stack onto the last).
 */
function landingSpot(
  dstEl: Element,
  size: number,
  isHit: boolean,
): { x: number; y: number } {
  const checkers = Array.from(dstEl.querySelectorAll<HTMLElement>('.checker'))
  const laneRect = dstEl.getBoundingClientRect()
  const cx = laneRect.left + laneRect.width / 2
  const isTop = dstEl.classList.contains('top')
  const dir = isTop ? 1 : -1

  if (isHit && checkers.length > 0) {
    return center(checkers[0].getBoundingClientRect())
  }
  if (checkers.length === 0) {
    const edge = isTop ? laneRect.top : laneRect.bottom
    return { x: cx, y: edge + dir * (size / 2 + 1) }
  }
  const lastCenter = center(
    checkers[checkers.length - 1].getBoundingClientRect(),
  )
  if (checkers.length >= 5) {
    return lastCenter
  }
  if (checkers.length >= 2) {
    const prev = center(checkers[checkers.length - 2].getBoundingClientRect())
    return {
      x: lastCenter.x + (lastCenter.x - prev.x),
      y: lastCenter.y + (lastCenter.y - prev.y),
    }
  }
  return { x: lastCenter.x, y: lastCenter.y + dir * (size + 2) }
}
